/**
 * Error handling for API responses — the single implementation for the whole app.
 *
 * Unified at CP21. There were two: this module (137 importers) and
 * `modules/ops-dashboard/shared/errorHandling.ts` (27). The ops one was the more capable parser and
 * became the canonical body; the portal's `ErrorResponseData` type is preserved below because
 * several files import it for casting.
 *
 * **Toast shape changed here.** `handleApiError(error, title)` now renders `title` as the toast
 * title with the server's message underneath, instead of using the server message as the title and
 * `title` only as a fallback. All 50 call sites across both halves already pass a translated,
 * action-specific string (`t('editCreditUpdateFailed')`, `t('steps.step1.link_document_failed')`) —
 * under the old shape those were silently discarded whenever the server returned any message, so
 * localized error titles were written and never shown. See §5.3 of `docs/ops-merge-context.md`.
 *
 * The parser swap was checked against the previous implementation on 15 representative payloads:
 * 11 identical, 4 changed, all four improvements — most importantly `{ response: 'msg', errors: null }`
 * (vouch-api's `error_response()`), which the old parser dropped entirely in favour of the generic
 * axios message.
 */

import { toast } from 'sonner';
import { htmlToText } from '@/modules/shared/utils';

/**
 * Error response types from backend API.
 * Supports multiple error formats commonly returned by Django/DRF backends.
 */
export type ErrorResponseData =
  | {
      /** Field-specific errors (object with field names as keys) */
      [field: string]: string[] | string;
    }
  | {
      message?: string;
      detail?: string;
      non_field_errors?: string[];
      errors?: string[] | { [field: string]: string[] | string };
      error?: string;
    }
  | string;

/** Convert a snake_case field name to Title Case: `policy_issued_date` → `Policy Issued Date`. */
const snakeToTitleCase = (str: string): string =>
  str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

/** Body keys that carry structured messages — not DRF field-validation keys. */
const SPECIAL_BODY_KEYS = new Set(['message', 'detail', 'error', 'errors', 'response', 'non_field_errors']);

/** First usable string out of a DRF field-error value: `["msg"]` | `"msg"` | `{k: "msg"}`. */
const firstErrorString = (val: unknown): string | null => {
  if (typeof val === 'string' && val.trim()) return val;
  if (Array.isArray(val)) {
    for (const item of val) {
      const s = firstErrorString(item);
      if (s) return s;
    }
    return null;
  }
  if (val && typeof val === 'object') {
    for (const v of Object.values(val as Record<string, unknown>)) {
      const s = firstErrorString(v);
      if (s) return s;
    }
  }
  return null;
};

/** Joins a nested field path for display: `['Car', 'Vehicle Weight']` → `Car → Vehicle Weight`. */
const FIELD_PATH_SEPARATOR = ' → ';

/** Error strings sitting directly on a field's value: `"msg"` or `["msg", "msg"]`. */
const directErrorStrings = (value: unknown): string[] => {
  if (typeof value === 'string') return value.trim() ? [value] : [];
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string' && !!item.trim());
  return [];
};

/**
 * Error maps nested inside a field's value: a nested serializer (`car: {…}`) or the per-item maps a
 * DRF list serializer returns (`drivers: [{…}, {…}]`).
 */
const nestedErrorMaps = (value: unknown): Record<string, unknown>[] => {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item),
    );
  }
  if (value && typeof value === 'object') return [value as Record<string, unknown>];
  return [];
};

/**
 * `Field Name: message` lines for a DRF error map.
 *
 * Nested maps are descended into and the whole path is kept, so a line names the field that actually
 * failed *and* the object it hangs off — `{car: {vehicle_weight: ["A valid integer is required."]}}`
 * reads as `Car → Vehicle Weight: A valid integer is required.`. Labelling with the wrapper alone
 * (`Car: …`) tells an operator nothing about which input to correct; labelling with the leaf alone
 * loses which object it belongs to once a body carries more than one.
 *
 * Envelope keys (`response`, `errors`) never reach here — the callers unwrap them first — so a path
 * only ever contains real field names.
 */
const collectFieldErrorLines = (map: Record<string, unknown>, path: string[] = []): string[] | null => {
  const entries = Object.entries(map);
  if (!entries.length) return null;

  const lines: string[] = [];
  for (const [field, value] of entries) {
    const fieldPath = [...path, snakeToTitleCase(field)];
    const fieldLines: string[] = [];

    const strings = directErrorStrings(value);
    if (strings.length) fieldLines.push(`${fieldPath.join(FIELD_PATH_SEPARATOR)}: ${strings.join(', ')}`);

    for (const nested of nestedErrorMaps(value)) {
      fieldLines.push(...(collectFieldErrorLines(nested, fieldPath) ?? []));
    }

    if (!fieldLines.length) return null; // not a field-error map — bail rather than emit a partial list
    lines.push(...fieldLines);
  }
  return lines;
};

/**
 * Render a field-error map as `Field Name: message` lines.
 *
 * Only bodies where *every* value carries an error string qualify — that keeps metadata bodies
 * such as `{status: 'error', code: 500}` from being mislabelled as validation errors.
 */
const formatFieldErrors = (map: unknown): string | null => {
  if (!map || typeof map !== 'object' || Array.isArray(map)) return null;
  const lines = collectFieldErrorLines(map as Record<string, unknown>);
  return lines?.length ? lines.join('\n') : null;
};

/** Field-validation keys at the top level of a DRF body (excluding structured message keys). */
const topLevelFieldErrorMap = (body: Record<string, unknown>): Record<string, unknown> | null => {
  const entries = Object.entries(body).filter(([key]) => !SPECIAL_BODY_KEYS.has(key));
  if (!entries.length) return null;
  return Object.fromEntries(entries);
};

/**
 * Extract a user-facing message from an API/axios error.
 *
 * @example `{ errors: { role: 'Cannot assign admin role' } }` → `"Role: Cannot assign admin role"`
 * @example `{ premium_received_within: ['Required'], policy_issued_date: ['Invalid'] }`
 *          → `"Premium Received Within: Required\nPolicy Issued Date: Invalid"`
 * @example `{ response: 'Quotation already cancelled', errors: null }`
 *          → `"Quotation already cancelled"`
 */
/**
 * Same as {@link extractErrorMessage}, for a request made with `responseType: 'blob'`.
 *
 * Axios hands back the *error* body as a Blob too, so `error.response.data` is an opaque object and
 * every key the normal extractor looks for is invisible — a 400 carrying
 * `{"response":"Invoice already paid.","errors":null}` surfaced as the generic fallback. Reading the
 * blob back to text is the only way to see it.
 *
 * `response` is checked before `errors` because this family of endpoints puts the human-readable
 * sentence there and leaves `errors` null; the full extractor still runs afterwards for bodies
 * shaped the usual way.
 */
export const extractBlobErrorMessage = async (
  error: unknown,
  fallback: string = 'Something went wrong',
): Promise<string> => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const text = await data.text();
      const body = JSON.parse(text) as Record<string, unknown>;
      const direct = firstErrorString(body.response) ?? firstErrorString(body.errors);
      if (direct) return direct;
      return extractErrorMessage({ response: { data: body } }, fallback);
    } catch {
      // Not JSON (an HTML error page, or an empty body) — nothing better than the fallback.
      return fallback;
    }
  }
  return extractErrorMessage(error, fallback);
};

export const extractErrorMessage = (error: unknown, fallback: string = 'Something went wrong'): string => {
  if (!error) return fallback;

  const err = error as { response?: { data?: unknown; statusText?: string }; data?: unknown; message?: unknown };
  const data = err?.response?.data ?? err?.data;

  if (typeof data === 'string' && data.trim()) {
    const stripped = htmlToText(data);
    // The length guard is what excludes full HTML error pages. Do NOT also reject strings that
    // merely start with '<' — that dropped usable bodies like `<p>Bad request</p>`.
    if (stripped && stripped.length <= 200) return stripped;
    if (stripped.length > 200) return fallback;
  }

  const body = data && typeof data === 'object' && !Array.isArray(data) ? (data as Record<string, unknown>) : null;

  if (body) {
    // 1. Nested `response` object — `{ response: { role: "..." }, errors: null }`
    if (body.response != null && typeof body.response === 'object' && !Array.isArray(body.response)) {
      const labelled = formatFieldErrors(body.response);
      if (labelled) return labelled;
    }

    // 2. Top-level field errors — `{ premium_received_within: ["Required field"] }`
    const topLevel = topLevelFieldErrorMap(body);
    if (topLevel) {
      const labelled = formatFieldErrors(topLevel);
      if (labelled) return labelled;
    }

    // 3. `errors` object/array — before generic `message` so field errors win
    const labelledErrors = formatFieldErrors(body.errors);
    if (labelledErrors) return labelledErrors;
    if (Array.isArray(body.errors) && body.errors.length) {
      return body.errors.map(String).join(', ');
    }
    const fromErrors = firstErrorString(body.errors);
    if (fromErrors) return fromErrors;

    // 4. `response` string — vouch-api message half of `error_response(message, errors)`
    if (typeof body.response === 'string' && body.response.trim()) return htmlToText(body.response);
    const fromResponse = firstErrorString(body.response);
    if (fromResponse) return fromResponse;

    // 5. Django non-field bucket
    const nonField = firstErrorString(body.non_field_errors);
    if (nonField) return nonField;

    // 6–8. Single-message keys — after structured errors
    if (typeof body.detail === 'string' && body.detail.trim()) return htmlToText(body.detail);
    if (typeof body.error === 'string' && body.error.trim()) return htmlToText(body.error);
    if (typeof body.message === 'string' && body.message.trim()) return htmlToText(body.message);

    // 9. Bare array body — `["msg"]`
    if (Array.isArray(data)) {
      const fromList = firstErrorString(data);
      if (fromList) return fromList;
    }

    // 10. Whole body as a field map (last resort). Only the *labelled* form — an unlabelled
    // `firstErrorString(body)` here surfaced meaningless tokens: `{status: 'error', code: 500}`
    // rendered as just "error". Falling through to statusText/message reads better.
    const labelledBody = formatFieldErrors(body);
    if (labelledBody) return labelledBody;
  } else if (Array.isArray(data)) {
    const fromList = firstErrorString(data);
    if (fromList) return fromList;
  }

  // Axios fallbacks last — would mask every API message above them.
  if (err?.response?.statusText) return err.response.statusText;
  if (typeof err?.message === 'string' && err.message) return err.message;
  return fallback;
};

/**
 * True when an API error means the session is not (or no longer) authorized.
 */
export const isUnauthorizedError = (error: unknown): boolean => {
  const err = error as { response?: { status?: number }; status?: number } | null;
  const status = err?.response?.status ?? err?.status;
  return status === 401 || status === 403;
};

/**
 * Show a toast with a fixed title and the parsed API message as description.
 *
 * @example
 * useMutation({
 *   mutationFn: linkDocuments,
 *   onError: (e) => handleApiError(e, t('steps.step1.link_document_failed')),
 * });
 */
/**
 * Show an API error as a toast: `title` on top, the server's message underneath.
 *
 * @param title Action-specific, already-translated. Rendered as the toast title.
 * @example handleApiError(err, t('steps.step1.link_document_failed'))
 */
export const handleApiError = (
  error: unknown,
  title: string = 'Something went wrong',
  options?: { duration?: number; description?: string },
): void => {
  const parsed = options?.description ?? extractErrorMessage(error, title);
  toast.error(title, {
    duration: options?.duration ?? 5000,
    description: parsed !== title ? parsed : undefined,
  });
};

/**
 * Field-level validation errors from API bodies (`errors` or legacy `response` maps).
 */
export const getApiFieldErrors = (error: unknown): Record<string, string> => {
  const data =
    (error as { response?: { data?: unknown }; data?: unknown })?.response?.data ?? (error as { data?: unknown })?.data;
  if (!data || typeof data !== 'object') return {};

  const out: Record<string, string> = {};
  const sources = [(data as { errors?: unknown }).errors, (data as { response?: unknown }).response];

  for (const src of sources) {
    if (src == null || typeof src !== 'object' || Array.isArray(src)) continue;
    for (const key of Object.keys(src as Record<string, unknown>)) {
      const val = (src as Record<string, unknown>)[key];
      if (Array.isArray(val) && val.length > 0) {
        out[key] = typeof val[0] === 'string' ? val[0] : String(val[0]);
      } else if (typeof val === 'string') {
        out[key] = val;
      }
    }
  }
  return out;
};
